import React, { useState, useEffect } from 'react';
import { providersAPI } from '../../api/providers';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Spinner from '../../components/common/Spinner';
import Input from '../../components/common/Input';
import Modal from '../../components/common/Modal';
import Textarea from '../../components/common/Textarea';
import {
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapPinIcon,
  PhoneIcon,
  ScissorsIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

const ManageProviders = () => {
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionError, setRejectionError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    filterProviders();
  }, [searchQuery, statusFilter, providers]);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      // Try admin endpoint first, fallback to regular endpoint
      try {
        const data = await providersAPI.getAllProviders();
        setProviders(data.providers || data);
      } catch (e) {
        // Fallback to regular providers endpoint
        const data = await providersAPI.getProviders();
        setProviders(data.providers || data);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const filterProviders = () => {
    let filtered = providers;

    if (searchQuery) {
      filtered = filtered.filter(
        (p) =>
          (p.businessName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
          (p.email?.toLowerCase() || '').includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      if (statusFilter === 'approved') {
        filtered = filtered.filter((p) => p.isApproved);
      } else if (statusFilter === 'pending') {
        filtered = filtered.filter((p) => !p.isApproved && p.verificationStatus === 'pending');
      } else {
        filtered = filtered.filter((p) => p.verificationStatus === statusFilter);
      }
    }

    setFilteredProviders(filtered);
  };

  const handleApprove = async (providerId) => {
    try {
      await providersAPI.approveProvider(providerId);
      toast.success('Provider approved successfully!');
      fetchProviders();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Failed to approve provider');
    }
  };

  const handleReject = (provider) => {
    setSelectedProvider(provider);
    setRejectionReason('');
    setRejectionError('');
    setShowModal(true);
  };

  const submitRejection = async () => {
    if (!rejectionReason.trim()) {
      setRejectionError('Please provide a rejection reason');
      return;
    }

    try {
      await providersAPI.rejectProvider(selectedProvider.id, rejectionReason);
      toast.success('Provider rejected');
      setShowModal(false);
      setRejectionReason('');
      setRejectionError('');
      fetchProviders();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Failed to reject provider');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl font-bold text-dark mb-2">Manage Providers</h1>
          <p className="text-gray-600">Review and approve provider applications</p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search providers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-bg focus:border-transparent"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-bg focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="documents_uploaded">Documents Uploaded</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </Card>

        {/* Providers List */}
        <div className="space-y-4">
          {filteredProviders.length === 0 ? (
            <Card className="text-center py-12">
              <p className="text-gray-600">No providers found</p>
            </Card>
          ) : (
            filteredProviders.map((provider) => {
              const expanded = expandedId === provider.id;
              return (
                <Card key={provider.id} style={{ padding: '10px 14px' }}>
                  {/* Compact row — tap anywhere to expand */}
                  <div
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => setExpandedId(expanded ? null : provider.id)}
                  >
                    <img
                      src={provider.photoUrl || 'https://via.placeholder.com/80'}
                      alt={provider.businessName}
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                      style={{ border: '1px solid var(--gray-4)' }}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="text-sm font-semibold text-dark truncate">
                          {provider.businessName}
                        </h3>
                        <Badge
                          variant={
                            provider.isApproved
                              ? 'success'
                              : provider.verificationStatus === 'rejected'
                                ? 'danger'
                                : 'warning'
                          }
                        >
                          {provider.isApproved
                            ? 'Approved'
                            : provider.verificationStatus.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-gray-600">
                        <span className="flex items-center gap-1 min-w-0">
                          <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--orange-9)' }} />
                          <span className="truncate">{provider.location || '—'}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--orange-9)' }} />
                          {provider.phone || '—'}
                        </span>
                        <span className="flex items-center gap-1">
                          <ScissorsIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--orange-9)' }} />
                          {provider.services?.length || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <StarSolid className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#facc15' }} />
                          {provider.rating.toFixed(1)}
                        </span>
                      </div>
                    </div>

                    <ChevronDownIcon
                      className="w-4 h-4 flex-shrink-0 transition-transform duration-200"
                      style={{
                        color: 'var(--gray-9)',
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </div>

                  {/* Expanded details */}
                  {expanded && (
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--gray-4)' }}>
                      {provider.email && (
                        <p className="text-xs text-gray-600 mb-1" style={{ textAlign: 'left' }}>
                          <span className="font-medium text-gray-700">Email:</span> {provider.email}
                        </p>
                      )}
                      {provider.description && (
                        <p className="text-sm text-gray-700 mb-3" style={{ textAlign: 'left' }}>
                          {provider.description}
                        </p>
                      )}

                      {!provider.isApproved && (
                        <div className="flex gap-3">
                          <Button
                            size="small"
                            onClick={() => handleApprove(provider.id)}
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="danger"
                            onClick={() => handleReject(provider)}
                          >
                            <XCircleIcon className="w-4 h-4" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setRejectionError(''); }}
        title="Reject Provider Application"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Please provide a reason for rejecting{' '}
            <span className="font-medium">{selectedProvider?.businessName}</span>
          </p>

          <Textarea
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => {
              setRejectionReason(e.target.value);
              setRejectionError('');
            }}
            placeholder="Explain why the application is being rejected..."
            rows={4}
            error={rejectionError}
            required
          />

          <div className="flex gap-3">
            <Button
              variant="outline"
              fullWidth
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              fullWidth
              onClick={submitRejection}
            >
              Reject Application
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ManageProviders;
